import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock'
import { activateFocusTrap, deactivateFocusTrap } from '../../utils/focus-tab'
import { createElement, render } from '../../utils/render'
import { createUiModalTemplate } from './template/main'
import { createDefaultTemplate } from './template/default'

const LEAVE_TIMEOUT = 400

const defaultClasses = Object.freeze({
  modal: 'ui-modal',
  close: 'ui-modal__close',
  leave: 'ui-modal-leave-to',
  iosOverflow: 'ios-safari-overflow'
})

const activeModals = []

const defaultOptions = Object.freeze({
  root: document.body,
  template: 'ui',
  isCloseButton: true,
  isCustom: false,
  isIndent: true,
  isCenter: false,
  isBottomSheet: false,
  wrapperClass: null,
  outerScroll: false,
  scrollingBlockClass: null,
  isAnimate: true,
  blockTriggerName: null,
  backgroundTheme: 'default'
})

export class UiModalVanila {
  /**
   * @param {HTMLElement} slot
   * @param {Object} options
   * @param {Object} classes
   */
  constructor(slot, options, classes = {}) {
    this._id = this._uuid()
    this._options = {
      ...defaultOptions,
      ...options
    }

    this.classes = {
      ...defaultClasses,
      ...classes
    }

    this._element = null
    this._slot = slot

    this.blockTriggerName = this._options.blockTriggerName
    this.closeCallbackHandler = this._options.closeHandler

    this._closeModalHandler = this._closeModalHandler.bind(this)
    this._keyDownEscHandler = this._keyDownEscHandler.bind(this)
    this.close = this.close.bind(this)
  }

  init() {
    this._create()
  }

  /**
   * Геттер для получения элемента
   * @returns {HTMLElement}
   */
  get element() {
    if (!this._element) {
      this._element = createElement(this.template)
    }

    return this._element
  }

  /**
   * Геттер для получения разметки элемента
   * @returns {string}
   */
  get template() {
    return this._options.template === 'ui'
      ? createUiModalTemplate(this._id, this._options)
      : createDefaultTemplate(this._id, this._options)
  }

  get contentWrapper() {
    return this.element.querySelector('[data-element=content-wrapper]')
  }

  get scrollingEl() {
    return this._options.scrollingBlockClass
      ? this.contentWrapper.querySelector(this._options.scrollingBlockClass)
      : this.contentWrapper
  }

  close() {
    this._destroy()
  }

  _create() {
    this._slot && this.contentWrapper.append(this._slot)

    this.beforeMounted()

    activeModals.push(this._id)

    disableBodyScroll(this.scrollingEl)
    document.body.classList.add(this.classes.iosOverflow) // fix for iOS

    render(this._options.root, this.element)

    activateFocusTrap(this.element)
    this.element.focus()

    this.element.addEventListener('click', this._closeModalHandler)
    document.addEventListener('keydown', this._keyDownEscHandler)

    this.mounted()
  }

  _destroy() {
    this.beforeUnmounted()

    this.element.classList.add(this.classes.leave)

    setTimeout(() => {
      this.element.classList.remove(this.classes.leave)

      deactivateFocusTrap(this.element)
      enableBodyScroll(this.scrollingEl)

      this.element.remove()
      this._element = null

      const currentModalIndex = activeModals.indexOf(this._id)
      if (currentModalIndex !== -1) {
        activeModals.splice(currentModalIndex, 1)
      }

      if (activeModals.length === 0) {
        document.removeEventListener('keydown', this._keyDownEscHandler)
        document.body.classList.remove(this.classes.iosOverflow) // fix for iOS
      }

      this.unmounted()
    }, LEAVE_TIMEOUT)

    this.closeCallbackHandler?.()
  }

  _checkCloseModalEvent(evt) {
    const target = evt.target
    const parent = target.closest(`.${this.classes.close}, .${this.classes.modal}`)
    const isButtonClose = parent ? Boolean(parent.closest(`.${this.classes.close}`)) : false

    return target === parent || isButtonClose
  }

  _keyDownEscHandler(evt) {
    if (evt.key === 'Escape' || evt.key === 'Esc') {
      const isLastModal = activeModals[activeModals.length - 1] === this._id

      if (isLastModal) {
        this.beforeClose()
        this.close()
      }
    }
  }

  _closeModalHandler(evt) {
    if (this._checkCloseModalEvent(evt)) {
      this.beforeClose()
      this.close()
    }
  }

  _uuid() {
    return ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, c =>
      (c ^ crypto.getRandomValues(new Uint8Array(1))[0] & 15 >> c / 4).toString(16)
    )
  }

  scrollTopContent() {
    this.scrollingEl.scrollTo(0, 0)
  }

  beforeMounted() {}

  mounted() {}

  beforeClose() {}

  beforeUnmounted() {}

  unmounted() {}
}
